import React, { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import ImportantMessage, { ImportantMessageTheme } from "../../../ui-components/molecules/important-message/importantMessage";
import {
  pickupDetailsSelector,
  changePickupStatus
} from "../../../slices/pickup/pickupDetailsSlice";
import {
  PickupRejectModel,
  PickupRejectSchema
} from "../../../app/data/pickup/models";
import XGSFormTextarea from "../../../ui-components/form/textarea/xgsFormTextarea";
import { PickupStatus } from "../../../app/data/common/route";
import ConfirmationModal from "../../../ui-components/confirmation-modal/confirmationModal";
import { ReactComponent as WarningIcon } from "../../../images/warning.svg";

import "./pickupDetails.scss";

export interface PickupRejectProps {
  show: boolean;
  pickupId: string;
  pickupNumber: string;
  onReject: () => void;
  onClose: () => void;
};

let initialValues: PickupRejectModel = {
  note: ""
};

const PickupReject: React.FC<PickupRejectProps> = ({ onClose, type, ...props }) => {
  const dispatch = useDispatch();
  const pickupDetailsState = useSelector(pickupDetailsSelector);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const formRef = useRef<FormikProps<PickupRejectModel>>(null);

  const onRejectPickup = () => {
    dispatch(changePickupStatus({
      pickupId: props.pickupId,
      status: PickupStatus.CANCELED,
      note: formRef.current?.values.note,
    },
      () => {
        toast.info("The pickup has been rejected!", { autoClose: 3000 });
        props.onReject();
        setShowConfirmation(false);
      },
      () => {
        toast.error("Failed to reject the pickup!");
      },
    ));
  };

  return (
    <SlideOutSidebar
      header="Pending Pickup Rejection"
      show={props.show}
      size="extrasmall"
      onClose={onClose}
      className="xgs-pickup-details__slideout"
    >
      <>
        <div className="xgs-pickup-details__slideout__inner-header">
          Reschedule Pickup {props.pickupNumber}
        </div>
        <ImportantMessage theme={ImportantMessageTheme.RED}>Do you really want to reject this pending pickup?</ImportantMessage>

        <div className="xgs-pickup-details__reschedule-form">
          <Formik
            onSubmit={() => setShowConfirmation(false)}
            initialValues={initialValues}
            validationSchema={PickupRejectSchema}
            enableReinitialize
            innerRef={formRef}
          >
            {(props: FormikProps<PickupRejectModel>) => (
              <Form>
                <XGSFormTextarea
                  name="note"
                  maxLength={256}
                  label="Reason"
                />

                <div className="xgs-pickup-details__slideout__buttons">
                  <Button
                    theme={ButtonThemes.gray}
                    onClick={onClose}
                    type="button"
                  >
                    Abort
                  </Button>
                  <Button
                    theme={ButtonThemes.blue}                    
                    type="button"
                    disabled={!props.isValid || !props.dirty}
                    spinner={pickupDetailsState.requestStarted && pickupDetailsState.requestCreator === "CHANGE_PICKUP_STATUS"}
                    onClick={() => { setShowConfirmation(true) }}
                  >
                    Proceed
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </>

      <ConfirmationModal
        fitContent
        opened={showConfirmation}
        header=""
        confirmButtonText="Reject"
        cancelButtonText="No"
        spinner={pickupDetailsState.requestStarted && pickupDetailsState.requestCreator === "CHANGE_PICKUP_STATUS"}
        onCancel={() => setShowConfirmation(false)}
        onConfirm={onRejectPickup}
        buttonTheme={ButtonThemes.red}
      >
        <div className="xgs-pickup-details__popup">
          <div className="xgs-pickup-details__popup__image xgs-pickup-details__popup__image--color_red">
            <WarningIcon/>
          </div>
          <div className="xgs-pickup-details__popup__text xgs-pickup-details__popup__text--centered">
            Do you really want to reject this pickup?
          </div>
        </div>
      </ConfirmationModal>
    </SlideOutSidebar>
  );
};

export default PickupReject;
